<template>
  <div class="campaign-subscribers-home d-flex flex-column">
    <h4 class="m-0 mb-2">Campaign Subscribers</h4>
    <div style="flex: 1;">
      <ConveyTable
        showSearch
        showFilter
        showDateRange
        showExport
        showCreate
        tableClassName="campaign-subscribers-table"
        searchMode="validPhoneSearch"
        :items="campaignSubscribers"
        :fields="fields"
        :placeholders="placeholders"
        :filterFields="filterFields"
        :filterOptions="filterOptions"
        :sortBy="sortBy"
        :sortDesc="sortDesc"
        :perPageOptions="perPageOptions"
        :perPage="perPage"
        :currentPage="currentPage"
        :totalRows="totalRows"
        :isLoading="isLoading"
        :isExportProcessing="isExportProcessing"
        @onFetch="(options) => fetchCampaignSubscribers(options)"
        @onExport="(options) => exportToCsv(options)"
        @onCreate="handleAddSubscriber"
        >
        <template #create-icon>
          <b-icon icon="plus-square"></b-icon>
        </template>
        <template #create-label>
          Add Subscriber
        </template>
        <template #cell(subscriber_number)="data">
          <router-link :to="`/campaigns/${campaignId}/subscribers/${data.item.subscriber_id}`">
            {{ prettyPhoneNumber(data.value) }}
          </router-link>
        </template>
        <template #cell(language)="data">
          <div class="d-flex align-items-center" v-if="data.value">
            <LanguageCode :languageCode="data.value" />
            <span class="ml-2">
              {{ dialectLabel(data.value) }}
            </span>
          </div>
        </template>
        <template #cell(status)="data">
          <span
            :class="`campaign-subscribers-table--${
              formatStatusToClass(data.value)
            }-text badge font-weight-medium text-capitalize ${
              isDarkMode ? 'dark' : ''
            }`"
            >{{ formatStatusValue(data.value) }}</span
          >
        </template>
        <template #cell(created_at)="data">
          <span class="text-nowrap">
            {{
              data.value
                ? formatDate(data.value, "MMM D, YYYY h:mm a")
                : "-"
            }}
          </span>
        </template>
        <template #cell(verified_at)="data">
          <span class="text-nowrap">
            {{
              data.value
                ? formatDate(data.value, "MMM D, YYYY h:mm a")
                : "-"
            }}
          </span>
        </template>
        <template #cell(attribution_source)="data">
          <span
            v-if="data.value"
            v-b-tooltip.hover
            :title="data.value"
            class="d-inline-block text-truncate text-capitalize"
            :style="{ 'width': '100px' }"
          >
            {{ data.value }}
          </span>
          <span v-else>-</span>
        </template>
        <template #cell(groups)="data">
          <div
            v-if="data.value.length > 0"
            class="d-flex flex-wrap justify-content-center align-items-center gap-1"
          >
            <b-badge
              v-for="group in data.value"
              :key="group"
            >
              {{ group }}
            </b-badge>
          </div>
          <span v-else>-</span>
        </template>
      </ConveyTable>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import axios from "axios";
import {
  runGraphqlQuery,
  formatDuration,
  downloadCSV,
  getBaseGraphqlServerUrl,
} from "@/graphql/utils";
import { makeCampaignSubscribersQuery } from "@/graphql/queries";
import LanguageCode from "@/components/common/LanguageCode.vue";
import ConveyTable from "@/components/ConveyTable";

export default {
  name: "CampaignSubscribersHome",
  mixins: [formatter, errorHandler],
  components: {
    LanguageCode,
    ConveyTable
  },
  data() {
    return {
      campaignSubscribers: [],
      isExportProcessing: false,
      fields: [
        { key: "subscriber_number", label: "Subscriber", sortable: false },
        { key: "groups", label: "Groups", sortable: false },
        { key: "verified_at", label: "Verified At", sortable: false },
        { key: "attribution_source", label: "Source", sortable: false },
        { key: "language", label: "Language", sortable: false },
        { key: "status", label: "Status", sortable: false },
        { key: "created_at", label: "Created At", sortable: true },
      ],
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      totalRows: 0,
      isLoading: false,
      sortBy: "created_at",
      sortDesc: true,
      filterOptions: {
        status: [
          {
            value: "pending",
            text: "Pending",
            label: "Pending",
          },
          {
            value: "verified",
            text: "Verified",
            label: "Verified",
          },
          {
            value: "undelivered",
            text: "Undelivered",
            label: "Undelivered",
          },
          {
            value: "optin",
            text: "Optin",
            label: "Optin",
          },
          {
            value: "optout",
            text: "Optout",
            label: "Optout",
          },
        ],
        attribution_source: [
          {
            value: "sms",
            text: "Sms",
            label: "Sms",
          },
          {
            value: "web",
            text: "Web",
            label: "Web",
          },
        ],
      },
      filterFields: [
        { name: 'status', label: 'Status' },
        { name: 'attribution_source', label: 'Source' },
      ],
      placeholders: {
        search: 'Search subscribers...',
        daterange: 'Select Date Range',
      },
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken", "isDarkMode"]),
    ...mapGetters("agency", ["currentAgencyId", "textDialects"]),
    campaignId() {
      return this.$route.params.campaignId;
    },
    dialects() {
      return this.textDialects || [];
    },
  },
  methods: {
    getDuration(data) {
      return formatDuration(data.duration);
    },
    abortFetchOperation() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchCampaignSubscribers(fetchOptions) {
      try {
        this.abortFetchOperation();
        this.fetchAbortController = new AbortController();
        const {
          perPage,
          currentPage,
          searchKey,
          filter,
          startDate,
          endDate,
          sortBy,
          sortDesc,
        } = fetchOptions;
        this.currentPage = currentPage;
        this.perPage = perPage;
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
        this.isLoading = true;
        const offset = (currentPage - 1) * perPage;

        const query = makeCampaignSubscribersQuery({
          startDate,
          endDate,
          offset,
          perPage,
          searchKey,
          campaignId: this.campaignId,
          filter,
          orderBy: this.sortBy,
          sortDesc,
        });

        const res = await runGraphqlQuery(query, this.fetchAbortController.signal);
        const responseData = res.data.data.getCampaignSubscribers;
        this.totalRows = Number(responseData.total);
        this.campaignSubscribers = responseData.data.map(subscriber => {
          const { groups } = subscriber;
          const groupList = groups ? groups.split(",") : [];
          return {
            ...subscriber,
            groups: groupList
          };
        });
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log("[CampaignSubscribersHome:fetchCampaignSubscribers] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    formatStatusValue(status) {
      const statusOption = this.filterOptions.status.find(s => s.value === status.toLowerCase());
      if (statusOption) {
        return statusOption.label;
      } else {
        return status;
      }
    },
    formatStatusToClass(status) {
      let statusClass = "";
      const statusOption = this.filterOptions.status.find(s => s.value === status.toLowerCase());
      if (statusOption) {
        statusClass = statusOption.label.toLowerCase().split(" ").join("-");
      } else {
        statusClass = "other";
      }
      return statusClass;
    },
    isValidUrl(value) {
      try {
        new URL(value);
        return true;
      } catch(err) {
        return false;
      }
    },
    formatSource(source) {
      if (this.isValidUrl(source)) {
        return "Web";
      } else {
        return "Sms"
      }
    },
    async exportToCsv(exportOptions) {
      try {
        const {
          searchKey,
          startDate,
          endDate,
          perPage,
          currentPage,
          sortBy,
          sortDesc,
          filter
        } = exportOptions;
        this.isExportProcessing = true;
        const baseURL = getBaseGraphqlServerUrl();
        const exportConversationUrl = `${baseURL}/export-campaign-subscribers`;

        const requestBody = JSON.stringify({
          campaignId: this.campaignId,
          searchKey,
          filters: filter,
          startDate,
          endDate,
          sortByField: sortBy,
          sortByOrder: sortDesc ? "DESC" : "ASC",
        });

        const response = await axios.post(
          exportConversationUrl,
          requestBody,
          {
            responseType: "blob",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${this.currentToken}`,
              "x-agency-id": `${this.currentAgencyId}`,
              "Content-Type": "application/json",
            },
          }
        );
        const blob = new Blob([response.data], { type: "text/csv" });
        this.isExportProcessing = false;
        downloadCSV(blob, "export-campaign-subscribers.csv");
      } catch (error) {
        this.isExportProcessing = false;
      }
    },
    handleAddSubscriber() {
      this.$router.push({ name: "New Campaign Subscriber" }).catch(() => {});
    }
  },
  beforeDestroy() {
    this.abortFetchOperation();
  }
};
</script>

<style lang="scss">
.campaign-subscribers-home {
  width: 100%;
  height: 100%;
  overflow: hidden;

  .campaign-subscribers-table {
    &--other-text {
      color: #667085;
      font-weight: 400;
      font-size: 0.875rem;
      display: block;
      &.dark {
        a {
          color: #9dc7ff !important;
        }
      }
    }
    &--verified-text {
      color: #fff;
      font-size: 0.875rem;
      background: #ababab;
      font-weight: 600;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        background: #9194a7 !important;
      }
    }
    &--pending-text,
    &--optin-text {
      color: #0d894f;
      font-size: 0.875rem;
      background: #e7f4ee;
      font-weight: 600;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        color: #0d894f;
        background: #dae5e0;
      }
    }
    &--undelivered-text,
    &--optout-text {
      color: #f04438;
      font-size: 0.875rem;
      background: #feedec;
      font-weight: 600;
      padding: 4px 12px 4px 12px;
      border-radius: 8px;
      &.dark {
        color: #ba490f;
        background: #e7cab4;
      }
    }
  }
}
</style>
